import { type FocusEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react'

const closeGraceMs = 220
const diagonalGraceMs = 260
const exitMs = 200
const landingMs = 340

export type AdminMenuPanelEntrance = 'glide' | 'nudge'

// An item only knows how it was activated, the wrapper around it knows where in the menu it sits
export type AdminMenuActivation = {
  isKeyboard: boolean
}

export type AdminMenuSelection = AdminMenuActivation &
  (
    | { origin: 'rail'; itemKey?: string; sectionKey?: never }
    | { origin: 'panel' | 'popup'; sectionKey?: string; itemKey?: never }
  )

// Enter and Space reach a link or a button as a click carrying no detail
export const isKeyboardActivation = (event: { detail: number }) => event.detail === 0

type IsDockingSelectionProps = {
  pushedKey: string | undefined
  landingKey: string | undefined
}

// Only the panel the selection was made in has somewhere to dock, everything else leaves the view
export const isDockingSelection = ({ pushedKey, landingKey }: IsDockingSelectionProps) =>
  Boolean(landingKey) && pushedKey === landingKey

type PushedPanel = {
  key: string
  entrance: AdminMenuPanelEntrance
}

type UseAdminMenuExpansionProps = {
  sectionKey: string | undefined
  hasPanel: (key: string) => boolean
}

type ResolvePreviewKeyProps = {
  hoverKey: string | null
  sectionKey: string | undefined
  hasPanel: (key: string) => boolean
}

export const resolvePreviewKey = ({ hoverKey, sectionKey, hasPanel }: ResolvePreviewKeyProps) => {
  const key = hoverKey ?? sectionKey
  return key && hasPanel(key) ? key : null
}

type ResolveHighlightedKeyProps = ResolvePreviewKeyProps & {
  isEngaged: boolean
}

export const resolveHighlightedKey = ({ isEngaged, ...props }: ResolveHighlightedKeyProps) =>
  isEngaged ? resolvePreviewKey(props) : null

type ResolveEntranceProps = {
  isOpening: boolean
  previewKey: string
  sectionKey: string | undefined
}

export const resolveEntrance = ({
  isOpening,
  previewKey,
  sectionKey,
}: ResolveEntranceProps): AdminMenuPanelEntrance =>
  isOpening && previewKey === sectionKey ? 'glide' : 'nudge'

export type AdminMenuPanelContentAnimation = 'none' | 'fade' | 'swap'

type ResolveContentAnimationProps = {
  hasSectionChanged: boolean
  isEntering: boolean
}

export const resolveContentAnimation = ({
  hasSectionChanged,
  isEntering,
}: ResolveContentAnimationProps): AdminMenuPanelContentAnimation => {
  if (!hasSectionChanged) {
    return 'none'
  }

  return isEntering ? 'fade' : 'swap'
}

type ShouldDeferHoverProps = {
  key: string
  previewKey: string | null
  isPreviewOpen: boolean
}

export const shouldDeferHover = ({ key, previewKey, isPreviewOpen }: ShouldDeferHoverProps) =>
  isPreviewOpen && key !== previewKey

export type AdminMenuEmbedBackdropReach = 'rail' | 'expandedRail' | 'panel' | 'pushedPanel'

type ResolveEmbedBackdropReachProps = {
  isExpanded: boolean
  isLanding: boolean
  hasPushedPanel: boolean
  hasRestingPanel: boolean
}

export const resolveEmbedBackdropReach = ({
  isExpanded,
  isLanding,
  hasPushedPanel,
  hasRestingPanel,
}: ResolveEmbedBackdropReachProps): AdminMenuEmbedBackdropReach => {
  if (hasPushedPanel && !isLanding) {
    return 'pushedPanel'
  }
  if (isExpanded) {
    return 'expandedRail'
  }
  return isLanding || hasRestingPanel ? 'panel' : 'rail'
}

export const useAdminMenuExpansion = ({ sectionKey, hasPanel }: UseAdminMenuExpansionProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)
  const [hoveredKey, setHoveredKey] = useState<string | null>(null)
  const [committedKey, setCommittedKey] = useState<string | null>(null)
  const [isLanding, setIsLanding] = useState(false)
  const [pushedPanel, setPushedPanel] = useState<PushedPanel | null>(null)
  const [isPushedPanelIn, setIsPushedPanelIn] = useState(false)

  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const diagonalTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const exitTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const landingTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  const isExpanded = (isHovered || isFocused) && !isDismissed
  const isEngaged = isExpanded || isLanding

  const previewKey = resolvePreviewKey({ hoverKey: committedKey, sectionKey, hasPanel })
  const isPreviewOpen = Boolean(previewKey) && isEngaged
  const highlightedKey = resolveHighlightedKey({
    hoverKey: hoveredKey,
    sectionKey,
    hasPanel,
    isEngaged,
  })

  // The close grace outlasts the panel's exit, so a timer scheduled before either ran has to
  // read both back as they are when it fires
  const latest = useRef({ isHovered, isFocused, sectionKey, pushedPanel })
  useEffect(() => {
    latest.current = { isHovered, isFocused, sectionKey, pushedPanel }
  }, [isHovered, isFocused, sectionKey, pushedPanel])

  const wasExpanded = useRef(isExpanded)

  useEffect(
    () => () => {
      clearTimeout(closeTimer.current)
      clearTimeout(diagonalTimer.current)
      clearTimeout(exitTimer.current)
      clearTimeout(landingTimer.current)
    },
    []
  )

  useEffect(() => {
    if (isLanding) {
      wasExpanded.current = isExpanded
      return
    }

    if (previewKey && isPreviewOpen) {
      const isOpening = isExpanded && !wasExpanded.current
      clearTimeout(exitTimer.current)
      setPushedPanel({
        key: previewKey,
        entrance: resolveEntrance({ isOpening, previewKey, sectionKey }),
      })
      setIsPushedPanelIn(true)
    } else {
      // The exit outlives the menu. Dropping the panel the moment the menu closes leaves the
      // resting panel to pop into a slot the pushed panel has not vacated yet.
      clearTimeout(exitTimer.current)
      setIsPushedPanelIn(false)
      exitTimer.current = setTimeout(() => setPushedPanel(null), exitMs)
    }

    wasExpanded.current = isExpanded
  }, [isPreviewOpen, previewKey, isExpanded, isLanding, sectionKey])

  const land = () => {
    if (!latest.current.pushedPanel) {
      return
    }

    clearTimeout(landingTimer.current)
    setIsLanding(true)
    landingTimer.current = setTimeout(() => {
      setIsLanding(false)
      setPushedPanel(null)
      setIsPushedPanelIn(false)
    }, landingMs)
  }

  // Closing the menu settles the resting panel into place, any other pushed panel exits
  const landIfResting = () => {
    const { pushedPanel: pushed, sectionKey: restingKey } = latest.current
    if (pushed?.key === restingKey) {
      land()
    }
  }

  const clearHover = () => {
    setHoveredKey(null)
    setCommittedKey(null)
  }

  const settle = ({ hovered, focused }: { hovered: boolean; focused: boolean }) => {
    setIsHovered(hovered)
    setIsFocused(focused)
    clearHover()

    if (!hovered && !focused) {
      landIfResting()
    }
  }

  const expand = () => {
    clearTimeout(closeTimer.current)
    clearTimeout(landingTimer.current)
    setIsLanding(false)
    setIsDismissed(false)
    setIsHovered(true)
  }

  const onItemHover = (key: string) => {
    clearTimeout(closeTimer.current)
    clearTimeout(diagonalTimer.current)

    setHoveredKey(key)

    if (!shouldDeferHover({ key, previewKey, isPreviewOpen })) {
      setCommittedKey(key)
      return
    }

    diagonalTimer.current = setTimeout(() => setCommittedKey(key), diagonalGraceMs)
  }

  // Picking a page in the panel collapses the rail while the panel docks. The pointer is left
  // resting over the panel and will fire no mouseleave, so only a dismissal keeps the rail
  // collapsed - the hover it never gave up would otherwise reopen it at once.
  // A rail selection leaves the pointer inside the rail, which stays expanded under it, so it
  // neither dismisses nor docks - the panel lands once the pointer leaves the rail. The overflow
  // popup only reaches the rail through React's portal tree, the pointer is outside it.
  // Keyboard selection is left open on purpose: focus stays on the item that was activated, and
  // docking the panel would unmount that item and strand the focus (WCAG 2.2 SC 1.4.13).
  const onSelect = ({
    sectionKey: landingKey,
    itemKey,
    isKeyboard,
    origin,
  }: AdminMenuSelection) => {
    clearTimeout(diagonalTimer.current)

    if (isKeyboard) {
      clearHover()
      return
    }

    if (origin === 'rail') {
      setHoveredKey(itemKey ?? null)
      setCommittedKey(itemKey ?? null)
      return
    }

    clearHover()

    setIsDismissed(true)

    if (isDockingSelection({ pushedKey: latest.current.pushedPanel?.key, landingKey })) {
      land()
    }
  }

  const scheduleClose = () => {
    clearTimeout(closeTimer.current)
    clearTimeout(diagonalTimer.current)
    closeTimer.current = setTimeout(
      () => settle({ hovered: false, focused: latest.current.isFocused }),
      closeGraceMs
    )
  }

  // A click focuses the item it lands on, which must not hold the menu open once the pointer
  // leaves. Only keyboard focus does, and :focus-visible is what tells the two apart.
  const focusIn = (event: FocusEvent<HTMLElement>) => {
    if (!event.target.matches(':focus-visible')) {
      return
    }

    setIsDismissed(false)
    setIsFocused(true)
  }

  // Only the rail opens the menu, the pushed panel just holds it open
  const railProps = {
    onMouseEnter: expand,
    onMouseLeave: scheduleClose,
    onFocus: focusIn,
  }

  const pushedPanelProps = {
    onMouseEnter: () => {
      clearTimeout(closeTimer.current)
      clearTimeout(diagonalTimer.current)
      setHoveredKey(committedKey)
    },
    onMouseLeave: scheduleClose,
    onFocus: focusIn,
  }

  const navProps = {
    onBlur: (event: FocusEvent<HTMLElement>) => {
      if (event.currentTarget.contains(event.relatedTarget)) {
        return
      }
      settle({ hovered: latest.current.isHovered, focused: false })
    },
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      // React routes portal events through the tree, so Escape in the overflow popup lands here too
      if (
        event.key !== 'Escape' ||
        !isExpanded ||
        !event.currentTarget.contains(event.target as Node)
      ) {
        return
      }

      setIsDismissed(true)
      clearHover()
      landIfResting()
    },
  }

  return {
    isExpanded,
    isEngaged,
    pushedPanel,
    isPushedPanelIn,
    isLanding,
    highlightedKey,
    onItemHover,
    onSelect,
    navProps,
    railProps,
    pushedPanelProps,
  }
}
